/* ============================================
   AgentConfigEditor — Agent Configuration Panel
   Editable system prompt, conversation rules,
   model parameters, and welcome message.
   ============================================ */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { AgentConfig, ReglaConversacion, TipoRegla, ScriptFase } from '@/lib/types'

/* ── Icons ── */
const icons = {
  settings: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  ),
  shield: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  sliders: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/>
    </svg>
  ),
  messageCircle: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
    </svg>
  ),
  plus: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  trash: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
    </svg>
  ),
  save: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
    </svg>
  ),
  check: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  alertTriangle: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  clipboard: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
    </svg>
  ),
  link: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
    </svg>
  ),
  code: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
    </svg>
  ),
  chevronDown: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
  chevronUp: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15"/>
    </svg>
  ),
}

/* ── Rule type config ── */
const RULE_TYPES: Record<TipoRegla, { label: string; color: string; icon: React.ReactNode }> = {
  prohibido: { label: 'Prohibido', color: 'var(--danger)', icon: icons.shield(14) },
  obligatorio: { label: 'Obligatorio', color: 'var(--success)', icon: icons.check(14) },
  limitacion: { label: 'Limitación', color: 'var(--warning)', icon: icons.alertTriangle(14) },
}

/* ── Tab config ── */
type ConfigTab = 'prompt' | 'rules' | 'model' | 'messages' | 'script'

const TABS: { key: ConfigTab; label: string; icon: (s?: number) => React.ReactNode }[] = [
  { key: 'rules', label: 'Reglas', icon: icons.shield },
  { key: 'script', label: 'Script', icon: icons.clipboard },
  { key: 'prompt', label: 'System Prompt', icon: icons.code },
  { key: 'model', label: 'Modelo', icon: icons.sliders },
  { key: 'messages', label: 'Mensajes', icon: icons.messageCircle },
]

interface AgentConfigEditorProps {
  agentId: number
}

export default function AgentConfigEditor({ agentId }: AgentConfigEditorProps) {
  const [config, setConfig] = useState<AgentConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [activeTab, setActiveTab] = useState<ConfigTab>('rules')
  const [isExpanded, setIsExpanded] = useState(true)

  // Editable fields (local state for editing)
  const [systemPrompt, setSystemPrompt] = useState('')
  const [rules, setRules] = useState<ReglaConversacion[]>([])
  const [temperature, setTemperature] = useState(0.7)
  const [maxTokens, setMaxTokens] = useState(1024)
  const [modelo, setModelo] = useState('gemini-2.5-flash')
  const [mensajeBienvenida, setMensajeBienvenida] = useState('')
  const [mensajeFueraHorario, setMensajeFueraHorario] = useState('')
  const [calendlyUrl, setCalendlyUrl] = useState('')
  const [scriptFases, setScriptFases] = useState<ScriptFase[]>([])

  // New rule form
  const [newRuleDesc, setNewRuleDesc] = useState('')
  const [newRuleType, setNewRuleType] = useState<TipoRegla>('prohibido')

  /* ── Load config ── */
  const loadConfig = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await supabase
        .from('agente_config')
        .select('*')
        .eq('id', agentId)
        .single()

      const cfg = (data ?? null) as unknown as AgentConfig | null

      if (cfg) {
        setConfig(cfg)
        setSystemPrompt(cfg.system_prompt || '')
        setRules(cfg.reglas_conversacion || [])
        setTemperature(cfg.temperature || 0.7)
        setMaxTokens(cfg.max_tokens || 1024)
        setModelo(cfg.modelo || 'gemini-2.5-flash')
        setMensajeBienvenida(cfg.mensaje_bienvenida || '')
        setMensajeFueraHorario(cfg.mensaje_fuera_horario || '')
        setCalendlyUrl(cfg.calendly_url || '')
        setScriptFases(cfg.script_ventas?.fases || [])
      }
    } catch (err) {
      console.error('Error loading agent config:', err)
    }
    setLoading(false)
  }, [agentId])

  useEffect(() => { loadConfig() }, [loadConfig])

  /* ── Save config ── */
  const handleSave = async () => {
    if (!config) return
    setSaving(true)
    setSaved(false)

    try {
      await (supabase.from('agente_config') as any).update({
        system_prompt: systemPrompt,
        reglas_conversacion: rules,
        temperature,
        max_tokens: maxTokens,
        modelo,
        mensaje_bienvenida: mensajeBienvenida || null,
        mensaje_fuera_horario: mensajeFueraHorario || null,
        calendly_url: calendlyUrl || null,
        script_ventas: { fases: scriptFases },
        updated_at: new Date().toISOString(),
      }).eq('id', config.id)

      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      loadConfig()
    } catch (err) {
      console.error('Error saving agent config:', err)
      alert('Error al guardar la configuración')
    }
    setSaving(false)
  }

  /* ── Rule management ── */
  const addRule = () => {
    if (!newRuleDesc.trim()) return
    setRules(prev => [...prev, { tipo: newRuleType, descripcion: newRuleDesc.trim(), activo: true }])
    setNewRuleDesc('')
  }

  const removeRule = (index: number) => {
    setRules(prev => prev.filter((_, i) => i !== index))
  }

  const toggleRule = (index: number) => {
    setRules(prev => prev.map((r, i) => i === index ? { ...r, activo: !r.activo } : r))
  }

  /* ── Has changes check ── */
  const hasChanges = config && (
    systemPrompt !== (config.system_prompt || '') ||
    JSON.stringify(rules) !== JSON.stringify(config.reglas_conversacion || []) ||
    temperature !== (config.temperature || 0.7) ||
    maxTokens !== (config.max_tokens || 1024) ||
    modelo !== (config.modelo || 'gemini-2.5-flash') ||
    mensajeBienvenida !== (config.mensaje_bienvenida || '') ||
    mensajeFueraHorario !== (config.mensaje_fuera_horario || '') ||
    calendlyUrl !== (config.calendly_url || '') ||
    JSON.stringify(scriptFases) !== JSON.stringify(config.script_ventas?.fases || [])
  )

  if (loading) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          Cargando configuración del agente...
        </div>
      </div>
    )
  }

  if (!config) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          <p style={{ fontSize: 15, marginBottom: 8 }}>No hay configuración de agente</p>
          <p style={{ fontSize: 13 }}>Ejecuta la migración SQL para crear la tabla <code>agente_config</code>.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginBottom: 24, overflow: 'visible' }}>
      {/* ── Header ── */}
      <div
        className="card-header"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12, cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            {icons.settings(18)} Configuración del Agente
          </h2>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            System prompt, reglas de conversación, parámetros del modelo
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {hasChanges && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
              background: 'color-mix(in srgb, var(--warning) 10%, transparent)', color: 'var(--warning)',
              border: '1px solid color-mix(in srgb, var(--warning) 20%, transparent)',
            }}>
              Sin guardar
            </span>
          )}
          {saved && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
              background: 'color-mix(in srgb, var(--success) 10%, transparent)', color: 'var(--success)',
              border: '1px solid color-mix(in srgb, var(--success) 20%, transparent)',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              {icons.check(12)} Guardado
            </span>
          )}
          <span style={{ color: 'var(--text-secondary)', transition: 'transform 0.2s' }}>
            {isExpanded ? icons.chevronUp(18) : icons.chevronDown(18)}
          </span>
        </div>
      </div>

      {/* ── Collapsible content ── */}
      {isExpanded && (
        <div style={{ padding: '0 20px 20px' }}>
          {/* Tabs */}
          <div style={{
            display: 'flex', gap: 4, marginBottom: 20, padding: 4,
            background: 'var(--bg-tertiary)', borderRadius: 10,
            border: '1px solid var(--border-color)',
          }}>
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={(e) => { e.stopPropagation(); setActiveTab(tab.key) }}
                style={{
                  flex: 1, padding: '8px 12px', border: 'none', borderRadius: 8,
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: activeTab === tab.key ? 'var(--bg-card)' : 'transparent',
                  color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-secondary)',
                  boxShadow: activeTab === tab.key ? '0 1px 4px rgba(var(--shadow-rgb), calc(.1 * var(--shadow-k)))' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                {tab.icon(14)} {tab.label}
              </button>
            ))}
          </div>

          {/* ═══ Tab: Rules ═══ */}
          {activeTab === 'rules' && (
            <div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
                Las reglas definen lo que Valentina <strong>puede</strong> y <strong>no puede</strong> hacer.
                Se inyectan automáticamente en el prompt del modelo.
              </p>

              {/* Rules list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {rules.map((rule, i) => {
                  const rt = RULE_TYPES[rule.tipo] || RULE_TYPES.limitacion
                  return (
                    <div
                      key={i}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                        background: rule.activo ? 'var(--bg-surface)' : 'var(--bg-tertiary)',
                        border: '1px solid var(--border-color)', borderRadius: 10,
                        opacity: rule.activo ? 1 : 0.5,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {/* Toggle */}
                      <button
                        onClick={() => toggleRule(i)}
                        style={{
                          width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                          background: rule.activo ? 'var(--success)' : 'var(--bg-tertiary)',
                          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                        }}
                      >
                        <div style={{
                          width: 16, height: 16, borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: 2, left: rule.activo ? 18 : 2,
                          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(var(--shadow-rgb), calc(.2 * var(--shadow-k)))',
                        }} />
                      </button>

                      {/* Type badge */}
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                        background: `${rt.color}15`, color: rt.color,
                        border: `1px solid ${rt.color}30`,
                        display: 'flex', alignItems: 'center', gap: 4,
                        flexShrink: 0, textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>
                        {rt.icon} {rt.label}
                      </span>

                      {/* Description */}
                      <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.4 }}>
                        {rule.descripcion}
                      </span>

                      {/* Delete */}
                      <button
                        onClick={() => removeRule(i)}
                        style={{
                          padding: 4, border: 'none', background: 'none', cursor: 'pointer',
                          color: 'var(--text-secondary)', flexShrink: 0,
                          transition: 'color 0.2s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
                        title="Eliminar regla"
                      >
                        {icons.trash(14)}
                      </button>
                    </div>
                  )
                })}

                {rules.length === 0 && (
                  <div style={{
                    padding: 24, textAlign: 'center', color: 'var(--text-secondary)',
                    background: 'var(--bg-surface)', borderRadius: 10,
                    border: '1px dashed var(--border-color)',
                  }}>
                    No hay reglas configuradas. Agrega una para definir los límites del agente.
                  </div>
                )}
              </div>

              {/* Add rule form */}
              <div style={{
                display: 'flex', gap: 8, alignItems: 'center',
                padding: 12, background: 'var(--bg-surface)',
                borderRadius: 10, border: '1px solid var(--border-color)',
              }}>
                <select
                  value={newRuleType}
                  onChange={e => setNewRuleType(e.target.value as TipoRegla)}
                  className="form-select"
                  style={{ width: 140, padding: '6px 10px', fontSize: 13 }}
                >
                  {Object.entries(RULE_TYPES).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
                <input
                  className="form-input"
                  value={newRuleDesc}
                  onChange={e => setNewRuleDesc(e.target.value)}
                  placeholder="Describe la regla... ej: No resolver tareas académicas"
                  style={{ flex: 1, padding: '6px 10px', fontSize: 13 }}
                  onKeyDown={e => e.key === 'Enter' && addRule()}
                />
                <button
                  className="btn btn-primary"
                  onClick={addRule}
                  disabled={!newRuleDesc.trim()}
                  style={{ padding: '6px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                >
                  {icons.plus(14)} Agregar
                </button>
              </div>
            </div>
          )}

          {/* ═══ Tab: Script ═══ */}
          {activeTab === 'script' && (
            <div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
                El script de ventas define las <strong>fases de la conversación</strong> que Valentina
                sigue para guiar al prospecto. Se inyecta automáticamente en el prompt.
              </p>

              {/* Calendly URL */}
              <div style={{
                padding: 14, marginBottom: 16, borderRadius: 10,
                background: 'var(--bg-surface)', border: '1px solid var(--border-color)',
              }}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  {icons.link(14)} URL de Calendly (Reunión de Diagnóstico)
                </label>
                <input
                  className="form-input"
                  value={calendlyUrl}
                  onChange={e => setCalendlyUrl(e.target.value)}
                  placeholder="https://calendly.com/tu-usuario/sesion-de-diagnostico"
                  style={{ fontSize: 13 }}
                />
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
                  Valentina compartirá este enlace cuando el prospecto quiera agendar una reunión.
                </p>
              </div>

              {/* Script phases */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {scriptFases.map((fase, i) => (
                  <div
                    key={i}
                    style={{
                      padding: 14, borderRadius: 10,
                      background: 'var(--bg-surface)', border: '1px solid var(--border-color)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <span style={{
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                        background: 'var(--color-primary)', color: 'var(--text-on-primary)',
                        fontSize: 12, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {i + 1}
                      </span>
                      <input
                        className="form-input"
                        value={fase.nombre}
                        onChange={e => {
                          const updated = [...scriptFases]
                          updated[i] = { ...updated[i], nombre: e.target.value }
                          setScriptFases(updated)
                        }}
                        placeholder="Nombre de la fase"
                        style={{ flex: 1, fontSize: 14, fontWeight: 600, padding: '6px 10px' }}
                      />
                      <button
                        onClick={() => setScriptFases(prev => prev.filter((_, j) => j !== i))}
                        style={{
                          padding: 4, border: 'none', background: 'none', cursor: 'pointer',
                          color: 'var(--text-secondary)', flexShrink: 0,
                          transition: 'color 0.2s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
                        title="Eliminar fase"
                      >
                        {icons.trash(14)}
                      </button>
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>
                        Objetivo
                      </label>
                      <input
                        className="form-input"
                        value={fase.objetivo}
                        onChange={e => {
                          const updated = [...scriptFases]
                          updated[i] = { ...updated[i], objetivo: e.target.value }
                          setScriptFases(updated)
                        }}
                        placeholder="¿Qué debe lograr esta fase?"
                        style={{ fontSize: 13, padding: '6px 10px' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>
                        Instrucciones para el AI
                      </label>
                      <textarea
                        className="form-textarea"
                        value={fase.instrucciones}
                        onChange={e => {
                          const updated = [...scriptFases]
                          updated[i] = { ...updated[i], instrucciones: e.target.value }
                          setScriptFases(updated)
                        }}
                        placeholder="Instrucciones específicas para Valentina en esta fase..."
                        style={{ minHeight: 60, fontSize: 13, resize: 'vertical' }}
                      />
                    </div>
                  </div>
                ))}

                {scriptFases.length === 0 && (
                  <div style={{
                    padding: 24, textAlign: 'center', color: 'var(--text-secondary)',
                    background: 'var(--bg-surface)', borderRadius: 10,
                    border: '1px dashed var(--border-color)',
                  }}>
                    No hay fases configuradas. Agrega fases para guiar la conversación.
                  </div>
                )}
              </div>

              {/* Add phase button */}
              <button
                className="btn btn-secondary"
                onClick={() => setScriptFases(prev => [...prev, {
                  id: `fase_${Date.now()}`,
                  nombre: '',
                  objetivo: '',
                  instrucciones: '',
                }])}
                style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {icons.plus(14)} Agregar Fase
              </button>
            </div>
          )}

          {/* ═══ Tab: System Prompt ═══ */}
          {activeTab === 'prompt' && (
            <div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
                El prompt principal que define la personalidad y comportamiento de Valentina.
                Usa <code style={{ fontSize: 12, padding: '1px 4px', background: 'var(--bg-tertiary)', borderRadius: 4 }}>{'{{nombre_agente}}'}</code>,{' '}
                <code style={{ fontSize: 12, padding: '1px 4px', background: 'var(--bg-tertiary)', borderRadius: 4 }}>{'{{empresa_nombre}}'}</code>,{' '}
                <code style={{ fontSize: 12, padding: '1px 4px', background: 'var(--bg-tertiary)', borderRadius: 4 }}>{'{{reglas_conversacion}}'}</code>,{' '}
                <code style={{ fontSize: 12, padding: '1px 4px', background: 'var(--bg-tertiary)', borderRadius: 4 }}>{'{{script_ventas}}'}</code> y{' '}
                <code style={{ fontSize: 12, padding: '1px 4px', background: 'var(--bg-tertiary)', borderRadius: 4 }}>{'{{calendly_url}}'}</code>{' '}
                como variables dinámicas.
              </p>
              <textarea
                className="form-textarea"
                value={systemPrompt}
                onChange={e => setSystemPrompt(e.target.value)}
                placeholder="System prompt del agente..."
                style={{
                  minHeight: 320, fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  fontSize: 13, lineHeight: 1.6, resize: 'vertical',
                }}
              />
              <div style={{
                display: 'flex', justifyContent: 'space-between', marginTop: 8,
                fontSize: 12, color: 'var(--text-secondary)',
              }}>
                <span>~{Math.ceil(systemPrompt.length / 4).toLocaleString()} tokens estimados</span>
                <span>{systemPrompt.length.toLocaleString()} caracteres</span>
              </div>
            </div>
          )}

          {/* ═══ Tab: Model ═══ */}
          {activeTab === 'model' && (
            <div>
              <div className="form-row" style={{ marginBottom: 20 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Modelo</label>
                  <select
                    className="form-select"
                    value={modelo}
                    onChange={e => setModelo(e.target.value)}
                  >
                    <option value="gemini-2.5-flash">Gemini 2.5 Flash (rápido, económico)</option>
                    <option value="gemini-2.5-pro">Gemini 2.5 Pro (avanzado)</option>
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash (legacy)</option>
                  </select>
                </div>
              </div>

              {/* Temperature slider */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label className="form-label" style={{ margin: 0 }}>Temperature</label>
                  <span style={{
                    fontSize: 14, fontWeight: 700, color: 'var(--color-primary)',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {temperature.toFixed(2)}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.05"
                  value={temperature}
                  onChange={e => setTemperature(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--color-primary)' }}
                />
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 11, color: 'var(--text-secondary)', marginTop: 4,
                }}>
                  <span>0.00 — Determinístico</span>
                  <span>1.00 — Creativo</span>
                  <span>2.00 — Muy creativo</span>
                </div>
                <div style={{
                  marginTop: 8, padding: '8px 12px', borderRadius: 8,
                  background: temperature > 1.0
                    ? 'color-mix(in srgb, var(--warning) 8%, transparent)'
                    : temperature < 0.3
                      ? 'color-mix(in srgb, var(--info) 8%, transparent)'
                      : 'color-mix(in srgb, var(--success) 8%, transparent)',
                  border: `1px solid ${temperature > 1.0
                    ? 'color-mix(in srgb, var(--warning) 20%, transparent)'
                    : temperature < 0.3
                      ? 'color-mix(in srgb, var(--info) 20%, transparent)'
                      : 'color-mix(in srgb, var(--success) 20%, transparent)'}`,
                  fontSize: 12, color: 'var(--text-secondary)',
                }}>
                  {temperature > 1.0
                    ? '⚠️ Temperature alta — respuestas más creativas pero menos predecibles. Recomendado: 0.5-0.8 para B2B.'
                    : temperature < 0.3
                      ? '❄️ Temperature baja — respuestas muy consistentes pero pueden sonar robóticas.'
                      : '✅ Temperature óptima para conversaciones B2B profesionales.'
                  }
                </div>
              </div>

              {/* Max tokens slider */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label className="form-label" style={{ margin: 0 }}>Max Tokens de Respuesta</label>
                  <span style={{
                    fontSize: 14, fontWeight: 700, color: 'var(--color-primary)',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {maxTokens.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="256"
                  max="16384"
                  step="128"
                  value={maxTokens}
                  onChange={e => setMaxTokens(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--color-primary)' }}
                />
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 11, color: 'var(--text-secondary)', marginTop: 4,
                }}>
                  <span>256 — Corto</span>
                  <span>4096 — Estándar</span>
                  <span>16384 — Largo</span>
                </div>
              </div>
            </div>
          )}

          {/* ═══ Tab: Messages ═══ */}
          {activeTab === 'messages' && (
            <div>
              <div className="form-group">
                <label className="form-label">Mensaje de Bienvenida</label>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Se muestra automáticamente cuando un visitante abre el chat por primera vez.
                </p>
                <textarea
                  className="form-textarea"
                  value={mensajeBienvenida}
                  onChange={e => setMensajeBienvenida(e.target.value)}
                  placeholder="¡Hola! Soy Valentina de 1Conecta 😊 ¿En qué puedo ayudarte?"
                  style={{ minHeight: 80, fontSize: 13 }}
                />
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label">Mensaje Fuera de Horario</label>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Se muestra cuando el visitante escribe fuera del horario de atención configurado.
                </p>
                <textarea
                  className="form-textarea"
                  value={mensajeFueraHorario}
                  onChange={e => setMensajeFueraHorario(e.target.value)}
                  placeholder="Hola, en este momento estamos fuera de horario. Déjanos tu mensaje y te responderemos a primera hora."
                  style={{ minHeight: 80, fontSize: 13 }}
                />
              </div>
            </div>
          )}

          {/* ── Save button ── */}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 8,
            marginTop: 20, paddingTop: 16,
            borderTop: '1px solid var(--border-color)',
          }}>
            {hasChanges && (
              <button
                className="btn btn-secondary"
                onClick={loadConfig}
                style={{ fontSize: 13 }}
              >
                Descartar cambios
              </button>
            )}
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !hasChanges}
              style={{
                fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                opacity: hasChanges ? 1 : 0.5,
              }}
            >
              {saving ? (
                <>Guardando...</>
              ) : saved ? (
                <>{icons.check(14)} Guardado</>
              ) : (
                <>{icons.save(14)} Guardar Configuración</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
